<script setup lang="ts">
  import ColorField from '@/components/customer/ColorField.vue'
  import PortalPreview from '@/components/customer/PortalPreview.vue'
  import WhiteLogo from '@/components/Logo.vue'
  import { useCustomerStore } from '@/store/customer.store'
  import { usePortalCustomizationStore } from '@/store/portal-customization.store'
  import type { Customer } from '@/types/nest/customer'
  import type { PortalCustomizationColors } from '@/types/nest/portal-customization'
  import {
    contrastRatio,
    DEFAULT_PRIMARY_BRAND_COLOR,
    DEFAULT_SECONDARY_BRAND_COLOR,
    isHexColor,
    isLightColor,
    MINIMUM_CONTRAST_RATIO,
    resolvePortalColors,
  } from '@/utils/portal-theme'
  import {
    Check,
    ConfirmationDialog,
    FileUpload,
    Info,
    Logo,
    UiButton,
    deepEqual,
    useToastStore,
  } from '@logistiek-zonder-papier/lzp-library'
  import { computed, onMounted, onUnmounted, ref, watch } from 'vue'

  type ColorKey = keyof PortalCustomizationColors

  type ColorGroup = {
    title: string
    description: string
    backgroundKey: ColorKey
    foregroundKey: ColorKey
    defaultBackground: string
  }

  type ContrastLevel = 'good' | 'fair' | 'poor'

  const MINIMUM_LARGE_TEXT_CONTRAST_RATIO = 3

  const ACCEPTED_LOGO_TYPES = ['image/png', 'image/jpeg', 'image/webp']

  const COLOR_KEYS: ColorKey[] = [
    'primaryBrandColor',
    'primaryForegroundColor',
    'secondaryBrandColor',
    'secondaryForegroundColor',
  ]

  const colorGroups: ColorGroup[] = [
    {
      title: 'Primaire kleur',
      description: 'Knoppen, links en het actieve menu-item.',
      backgroundKey: 'primaryBrandColor',
      foregroundKey: 'primaryForegroundColor',
      defaultBackground: DEFAULT_PRIMARY_BRAND_COLOR,
    },
    {
      title: 'Secundaire kleur',
      description: 'De zijbalk met het logo, het menu en je account.',
      backgroundKey: 'secondaryBrandColor',
      foregroundKey: 'secondaryForegroundColor',
      defaultBackground: DEFAULT_SECONDARY_BRAND_COLOR,
    },
  ]

  const { customer } = defineProps<{ customer: Customer }>()

  const portalCustomizationStore = usePortalCustomizationStore()
  const customerStore = useCustomerStore()
  const toastStore = useToastStore()

  const colors = ref<PortalCustomizationColors>(savedColors())
  const savingColors = ref<boolean>(false)

  const logoFile = ref<File | undefined>()
  const pendingLogoUrl = ref<string | null>(null)
  const savingLogo = ref<boolean>(false)
  const showRemoveLogoDialog = ref<boolean>(false)

  const validColors = computed<PortalCustomizationColors>(() => {
    const saved = savedColors()
    return Object.fromEntries(
      COLOR_KEYS.map(key => {
        const value = colors.value[key]
        return [key, value == null || isHexColor(value) ? value : saved[key]]
      })
    ) as PortalCustomizationColors
  })

  const resolvedColors = computed(() => resolvePortalColors(validColors.value))

  const hasColorErrors = computed<boolean>(() =>
    COLOR_KEYS.some(key => colors.value[key] != null && !isHexColor(colors.value[key]))
  )

  const hasColorChanges = computed<boolean>(() => !deepEqual(colors.value, savedColors()))

  const logoFileError = computed<string | null>(() =>
    logoFile.value && !ACCEPTED_LOGO_TYPES.includes(logoFile.value.type)
      ? 'Dit bestandstype wordt niet ondersteund. Gebruik een PNG, JPG of WEBP.'
      : null
  )

  const logoTileUsesWhiteLogo = computed<boolean>(() =>
    isLightColor(resolvedColors.value.secondaryForegroundColor)
  )

  function savedColors(): PortalCustomizationColors {
    const saved = portalCustomizationStore.portalCustomization
    return {
      primaryBrandColor: saved?.primaryBrandColor ?? null,
      primaryForegroundColor: saved?.primaryForegroundColor ?? null,
      secondaryBrandColor: saved?.secondaryBrandColor ?? null,
      secondaryForegroundColor: saved?.secondaryForegroundColor ?? null,
    }
  }

  function groupContrast(group: ColorGroup): { ratio: number; level: ContrastLevel } {
    const ratio = contrastRatio(
      resolvedColors.value[group.backgroundKey],
      resolvedColors.value[group.foregroundKey]
    )

    if (ratio >= MINIMUM_CONTRAST_RATIO) return { ratio, level: 'good' }
    if (ratio >= MINIMUM_LARGE_TEXT_CONTRAST_RATIO) return { ratio, level: 'fair' }
    return { ratio, level: 'poor' }
  }

  const contrastLabels: Record<ContrastLevel, string> = {
    good: 'Goed leesbaar',
    fair: 'Matig leesbaar',
    poor: 'Slecht leesbaar',
  }

  function resetColors() {
    colors.value = savedColors()
  }

  async function saveColors() {
    if (hasColorErrors.value) return

    const saved = savedColors()
    const changedColors = Object.fromEntries(
      COLOR_KEYS.filter(key => colors.value[key] !== saved[key]).map(key => [
        key,
        colors.value[key],
      ])
    )

    savingColors.value = true
    try {
      await portalCustomizationStore.patchPortalCustomization(customer.id, changedColors)
      colors.value = savedColors()
      toastStore.success('Kleuren opgeslagen')
    } finally {
      savingColors.value = false
    }
  }

  async function refreshLogo() {
    await customerStore.fetchCurrentCustomer()
    await portalCustomizationStore.fetchLogo(customerStore.currentCustomer?.imageFileId)
  }

  async function saveLogo() {
    if (!logoFile.value || logoFileError.value) return

    savingLogo.value = true
    try {
      await portalCustomizationStore.uploadLogo(customer.id, logoFile.value)
      await refreshLogo()
      logoFile.value = undefined
      toastStore.success('Logo opgeslagen')
    } finally {
      savingLogo.value = false
    }
  }

  async function removeLogo() {
    await portalCustomizationStore.removeLogo(customer.id)
    await refreshLogo()
    showRemoveLogoDialog.value = false
    toastStore.success('Het LZP-logo wordt weer gebruikt')
  }

  watch(
    () => portalCustomizationStore.portalCustomization,
    () => {
      if (!hasColorChanges.value) colors.value = savedColors()
    }
  )

  watch(validColors, current => portalCustomizationStore.previewColors(current), { deep: true })

  watch(logoFile, file => {
    if (pendingLogoUrl.value) URL.revokeObjectURL(pendingLogoUrl.value)
    pendingLogoUrl.value =
      file && ACCEPTED_LOGO_TYPES.includes(file.type) ? URL.createObjectURL(file) : null
  })

  onMounted(async () => {
    if (!portalCustomizationStore.portalCustomization) {
      await portalCustomizationStore.fetchPortalCustomization(customer.id)
    }
    colors.value = savedColors()
  })

  onUnmounted(() => {
    portalCustomizationStore.resetActiveColors()
    if (pendingLogoUrl.value) URL.revokeObjectURL(pendingLogoUrl.value)
  })
</script>

<template>
  <div class="branding-container">
    <div class="branding">
      <div class="settings">
        <section class="panel" aria-labelledby="colors-title">
          <header class="panel-header">
            <h2 id="colors-title" class="panel-title">Kleuren</h2>
            <p class="panel-description">
              Stem de nieuwe omgeving van het portaal af op je huisstijl. Laat een veld leeg om de
              standaardkleur te gebruiken; zonder tekstkleur kiest het portaal automatisch zwart of
              wit.
            </p>
          </header>

          <div v-for="group in colorGroups" :key="group.title" class="color-group">
            <div class="group-header">
              <div class="group-heading">
                <h3 class="group-title">{{ group.title }}</h3>
                <p class="group-description">{{ group.description }}</p>
              </div>

              <span
                class="contrast"
                :class="groupContrast(group).level"
                :title="`Contrast ${groupContrast(group).ratio.toFixed(1)}:1 (WCAG AA vraagt minimaal ${MINIMUM_CONTRAST_RATIO}:1)`"
              >
                <Check v-if="groupContrast(group).level === 'good'" class="contrast-icon" />
                <Info v-else class="contrast-icon" />
                {{ contrastLabels[groupContrast(group).level] }}
                <span class="contrast-ratio">{{ groupContrast(group).ratio.toFixed(1) }}:1</span>
              </span>
            </div>

            <div class="group-fields">
              <ColorField
                v-model="colors[group.backgroundKey]"
                label="Achtergrond"
                :default-color="group.defaultBackground"
                :default-label="`Standaard (${group.defaultBackground.toUpperCase()})`"
              />
              <ColorField
                v-model="colors[group.foregroundKey]"
                label="Tekst"
                :default-color="resolvedColors[group.foregroundKey]"
                default-label="Automatisch"
              />
            </div>
          </div>

          <footer class="panel-footer">
            <span v-if="hasColorChanges" class="unsaved">
              <span class="unsaved-dot" />
              Niet opgeslagen
            </span>
            <UiButton label="Annuleren" :disabled="!hasColorChanges" @click="resetColors" />
            <UiButton
              label="Opslaan"
              variant="prominent"
              :disabled="!hasColorChanges || hasColorErrors || savingColors"
              @click="saveColors"
            />
          </footer>
        </section>

        <section class="panel" aria-labelledby="logo-title">
          <header class="panel-header">
            <h2 id="logo-title" class="panel-title">Logo</h2>
            <p class="panel-description">
              Het logo staat linksboven in het portaal en op afleverdocumenten. Een liggend logo met
              een transparante achtergrond werkt het best.
            </p>
          </header>

          <div class="current-logo">
            <div class="logo-tile">
              <img
                v-if="portalCustomizationStore.logoUrl"
                class="logo-tile-image"
                :src="portalCustomizationStore.logoUrl"
                alt="Huidig logo"
              />
              <WhiteLogo v-else-if="logoTileUsesWhiteLogo" class="logo-tile-default" />
              <Logo v-else class="logo-tile-default" />
            </div>

            <div class="current-logo-text">
              <span class="current-logo-title">
                {{ portalCustomizationStore.logoUrl ? 'Eigen logo' : 'LZP-logo' }}
              </span>
              <span class="current-logo-description">
                {{
                  portalCustomizationStore.logoUrl
                    ? 'Wordt nu gebruikt in het portaal'
                    : 'Standaard, er is nog geen eigen logo'
                }}
              </span>
            </div>

            <UiButton
              v-if="portalCustomizationStore.logoUrl"
              label="Verwijderen"
              variant="link"
              @click="showRemoveLogoDialog = true"
            />
          </div>

          <div class="upload">
            <FileUpload v-model="logoFile" />
            <span v-if="logoFileError" class="upload-error">{{ logoFileError }}</span>
            <span v-else class="upload-hint">PNG, JPG of WEBP</span>
          </div>

          <footer class="panel-footer">
            <UiButton label="Annuleren" :disabled="!logoFile" @click="logoFile = undefined" />
            <UiButton
              label="Logo opslaan"
              variant="prominent"
              :disabled="!logoFile || !!logoFileError || savingLogo"
              @click="saveLogo"
            />
          </footer>
        </section>
      </div>

      <aside class="preview-column" aria-labelledby="preview-title">
        <div class="preview-sticky">
          <header class="preview-header">
            <h2 id="preview-title" class="panel-title">Voorbeeld</h2>
            <p class="panel-description">
              Zo ziet het portaal eruit. Wijzigingen zie je ook direct in het echte menu; ze gelden
              pas voor iedereen na het opslaan.
            </p>
          </header>

          <PortalPreview
            :colors="validColors"
            :logo-url="pendingLogoUrl ?? portalCustomizationStore.logoUrl"
          />
        </div>
      </aside>
    </div>

    <ConfirmationDialog
      v-model:show="showRemoveLogoDialog"
      title="Logo verwijderen"
      body="Het portaal en de afleverdocumenten gebruiken daarna weer het LZP-logo."
      confirm-button-text="Verwijderen"
      confirm-button-color="negative"
      :confirm="removeLogo"
    />
  </div>
</template>

<style scoped lang="css">
  .branding-container {
    container-type: inline-size;
    width: 100%;
  }

  .branding {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(16.25rem, 21.25rem);
    gap: var(--spacing-m);
    align-items: start;
    max-width: 73.75rem;
  }

  .settings {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-m);
    min-width: 0;
  }

  .panel {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    padding: 1.5rem;
    border: 1px solid var(--colours-grey-3);
    border-radius: 1rem;
    background: var(--colours-grey-1);
    box-shadow: 0 1px 2px var(--colours-shadow-1);
  }

  .panel-header,
  .preview-header {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .panel-title {
    margin: 0;
    color: var(--colours-grey-5);
    font-size: 1.125rem;
    font-weight: 700;
    line-height: 1.75rem;
  }

  .panel-description {
    margin: 0;
    max-width: 60ch;
    color: var(--colours-grey-4);
    font-size: 0.875rem;
    line-height: 1.25rem;
  }

  .color-group {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-s);
    padding-top: 1.5rem;
    border-top: 1px solid var(--colours-grey-3);
  }

  .group-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: var(--spacing-s);
  }

  .group-heading {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
  }

  .group-title {
    margin: 0;
    color: var(--colours-grey-5);
    font-size: 1rem;
    font-weight: 600;
    line-height: 1.5rem;
  }

  .group-description {
    margin: 0;
    color: var(--colours-grey-4);
    font-size: 0.875rem;
    line-height: 1.25rem;
  }

  .group-fields {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: var(--spacing-s);
  }

  .contrast {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    gap: 0.375rem;
    padding: 0.25rem 0.625rem;
    border-radius: 999px;
    font-size: 0.8125rem;
    font-weight: 600;
    line-height: 1.25rem;
    white-space: nowrap;
  }

  .contrast.good {
    background: var(--colours-labels-green);
    color: color-mix(in srgb, var(--colours-support-positive) 55%, var(--colours-grey-5) 45%);
  }

  .contrast.fair {
    background: var(--colours-labels-orange);
    color: color-mix(in srgb, var(--colours-support-warning) 60%, var(--colours-grey-5) 40%);
  }

  .contrast.poor {
    background: var(--colours-labels-red);
    color: color-mix(in srgb, var(--colours-support-negative) 70%, var(--colours-grey-5) 30%);
  }

  .contrast-icon {
    width: 0.875rem;
    height: 0.875rem;
  }

  .contrast-ratio {
    font-weight: 500;
    font-variant-numeric: tabular-nums;
    opacity: 0.75;
  }

  .panel-footer {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: var(--spacing-xs);
    padding-top: 1.5rem;
    border-top: 1px solid var(--colours-grey-3);
  }

  .unsaved {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    margin-right: auto;
    color: var(--colours-grey-4);
    font-size: 0.875rem;
  }

  .unsaved-dot {
    width: 0.5rem;
    height: 0.5rem;
    border-radius: 50%;
    background: var(--colours-support-warning);
  }

  .current-logo {
    display: flex;
    align-items: center;
    gap: var(--spacing-s);
  }

  .logo-tile {
    display: grid;
    flex-shrink: 0;
    place-items: center;
    width: 10rem;
    height: 4rem;
    padding: var(--spacing-xs) 0.75rem;
    border: 1px solid var(--colours-grey-3);
    border-radius: 0.75rem;
    background: v-bind('resolvedColors.secondaryBrandColor');
  }

  .logo-tile-image {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }

  .logo-tile-default {
    height: 2.25rem;
    width: auto;
  }

  .current-logo-text {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
  }

  .current-logo-title {
    color: var(--colours-grey-5);
    font-weight: 600;
  }

  .current-logo-description {
    color: var(--colours-grey-4);
    font-size: 0.875rem;
  }

  .upload {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
  }

  .upload-hint,
  .upload-error {
    font-size: 0.8125rem;
    line-height: 1.25rem;
  }

  .upload-hint {
    color: var(--colours-grey-4);
  }

  .upload-error {
    color: var(--colours-support-negative);
  }

  .preview-column {
    align-self: stretch;
    min-width: 0;
  }

  .preview-sticky {
    position: sticky;
    top: calc(var(--top-bar-height) + var(--spacing-s));
    display: flex;
    flex-direction: column;
    gap: var(--spacing-s);
  }

  @container (max-width: 51.25rem) {
    .branding {
      grid-template-columns: minmax(0, 1fr);
    }

    .preview-sticky {
      position: static;
      max-width: 30rem;
    }
  }

  @container (max-width: 32.5rem) {
    .group-fields {
      grid-template-columns: minmax(0, 1fr);
    }

    .group-header,
    .current-logo {
      flex-wrap: wrap;
    }
  }
</style>
